import { toError } from '#lib/util/error-handling/to-error.ts';
import { events } from 'fetch-event-stream';
import type {
	AssetRef,
	ChatMessage,
	ClassificationInfo,
	ToolCall,
	TopicInfo
} from '#src/types/chat.ts';
import type { RetrievalContext } from '#src/types/documents.ts';
import type { SecondarySource, SourceDocument } from '#src/types/sources.ts';

export interface StreamEvent {
	message: ChatMessage;
	eventData: Record<string, unknown>;
	eventType?: string;
	sources?: SourceDocument[];
	secondarySources?: SecondarySource[];
	retrievalContext?: RetrievalContext;
}

export async function* processStreamedResponse(
	response: Response,
	tempMessage: ChatMessage,
	abortController?: AbortController
): AsyncGenerator<StreamEvent> {
	const stream = events(response, abortController?.signal);
	const message = { ...tempMessage };

	for await (const event of stream) {
		const eventType = event.event;
		const eventData = JSON.parse(event.data);

		switch (eventType) {
			case 'conversation.created':
				break;
			case 'rag.started':
				message.status = eventType;
				message.id = (eventData.message_id as string) || message.id;
				break;
			case 'rag.reasoning':
				message.status = 'reasoning';
				message.reasoning_content = (message.reasoning_content || '').concat(
					eventData.reasoning_content || ''
				);
				break;
			case 'rag.content':
				message.status = 'in_progress';
				message.content = message.content.concat((eventData.content as string) || '');
				message.currentToolCallIndex = undefined;
				break;
			case 'rag.done':
				message.status = 'completed';
				break;
			case 'rag.retrieval_context':
				// Emit retrieval context for client-side storage (not persisted)
				yield { message, eventData, eventType, retrievalContext: eventData as RetrievalContext };
				continue;
			case 'rag.sources':
				// Emit sources for client-side storage (persisted)
				yield { message, eventData, eventType, sources: eventData.sources as SourceDocument[] };
				continue;
			case 'rag.secondary_sources':
				yield {
					message,
					eventData,
					eventType,
					secondarySources: eventData.secondary_sources as SecondarySource[]
				};
				continue;
			case 'rag.tool_call': {
				message.status = eventType;
				const toolCall = { ...(eventData as ToolCall), status: 'running' as const };
				message.toolCalls = [...(message.toolCalls || []), toolCall];
				message.currentToolCallIndex = message.toolCalls.length - 1;
				// Any tool call that declares itself as asset-producing already has a
				// "generating" placeholder created and persisted server-side (see
				// NewAssetInterceptor in the backend) — no per-tool-name knowledge needed here.
				const asset = (eventData as { asset?: AssetRef }).asset;
				if (asset) {
					message.assets = [...(message.assets || []), asset];
				}
				break;
			}
			case 'rag.tool_result': {
				message.status = eventType;
				const { call_id, result_count, status, error, asset } = eventData as {
					call_id: string;
					result_count: number;
					status: 'completed' | 'error';
					error?: string;
					asset?: AssetRef;
				};
				const toolCall = message.toolCalls?.find((tc) => tc.call_id === call_id);
				if (toolCall) {
					toolCall.result_count = result_count;
					toolCall.status = status;
					if (error) {
						toolCall.error = error;
					}
				}
				// Any tool that produced a downloadable asset (e.g. generate_image) has
				// it saved server-side before this event is emitted, so `asset` already
				// carries a final, permanent representation — never a temporary URL.
				if (asset) {
					const existing = message.assets?.find((a) => a.call_id === asset.call_id);
					if (existing) {
						Object.assign(existing, asset);
					} else {
						message.assets = [...(message.assets || []), asset];
					}
				}
				break;
			}
			case 'rag.available_tools':
				message.available_tools = eventData.tools as string[];
				break;
			case 'rag.classification':
				message.classification = eventData as ClassificationInfo;
				break;
			case 'rag.topic':
				message.topic = eventData as unknown as TopicInfo;
				break;
			case 'rag.timing':
				message.timings = eventData as { total_seconds: number; steps?: Record<string, number> };
				break;
			case 'rag.error':
			case 'error':
				throw toError(eventData);
		}

		yield { message, eventData, eventType };
	}

	return message;
}
